import React, { Fragment } from "react";
import { substituteVariables } from "./recoveryHelpers";

let _nodeKey = 0;
const nk = () => `wa-${_nodeKey++}`;

/**
 * Insere marcação ao redor da seleção (ou no cursor). Mantém texto puro compatível com WhatsApp.
 */
export function wrapTextareaSelection(textarea, fullValue, onNewValue, before, after) {
  if (!textarea) return;
  const start = textarea.selectionStart ?? 0;
  const end = textarea.selectionEnd ?? 0;
  const sel = fullValue.slice(start, end);
  const next = fullValue.slice(0, start) + before + sel + after + fullValue.slice(end);
  onNewValue(next);
  const ns = start + before.length;
  const ne = ns + sel.length;
  requestAnimationFrame(() => {
    try {
      textarea.focus();
      textarea.setSelectionRange(ns, ne);
    } catch {
      /* ignore */
    }
  });
}

/** Insere no início de cada linha do bloco selecionado */
export function prefixLinesInTextarea(textarea, fullValue, onNewValue, prefix) {
  if (!textarea) return;
  const start = textarea.selectionStart ?? 0;
  const end = textarea.selectionEnd ?? 0;
  const beforeCaret = fullValue.slice(0, start);
  const lineStart = beforeCaret.lastIndexOf("\n") + 1;
  const block = fullValue.slice(lineStart, end);
  const lines = block.split("\n");
  const nextBlock = lines.map((ln) => (ln.startsWith(prefix) ? ln : prefix + ln)).join("\n");
  const next = fullValue.slice(0, lineStart) + nextBlock + fullValue.slice(end);
  onNewValue(next);
  requestAnimationFrame(() => {
    try {
      textarea.focus();
      textarea.setSelectionRange(lineStart + nextBlock.length, lineStart + nextBlock.length);
    } catch {
      /* ignore */
    }
  });
}

/** Nova linha com prefixo de lista */
export function insertListLine(textarea, fullValue, onNewValue, linePrefix) {
  if (!textarea) return;
  const start = textarea.selectionStart ?? 0;
  const before = fullValue.slice(0, start);
  const after = fullValue.slice(start);
  const needsNl = before.length > 0 && !before.endsWith("\n");
  const insert = `${needsNl ? "\n" : ""}${linePrefix}`;
  const next = before + insert + after;
  onNewValue(next);
  const pos = start + insert.length;
  requestAnimationFrame(() => {
    try {
      textarea.focus();
      textarea.setSelectionRange(pos, pos);
    } catch {
      /* ignore */
    }
  });
}

function parseLineBlocks(text) {
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line === "") {
      let blanks = 0;
      while (i < lines.length && lines[i] === "") {
        blanks++;
        i++;
      }
      if (blanks > 0) blocks.push({ type: "blank", count: blanks });
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ""));
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }
    if (/^[\*\-•]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[\*\-•]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[\*\-•]\s/, ""));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }
    if (/^>\s?/.test(line)) {
      const q = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        q.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ type: "quote", lines: q });
      continue;
    }
    const para = [];
    while (i < lines.length && lines[i] !== "" && !/^\d+\.\s|^[\*\-•]\s|^>\s?/.test(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    blocks.push({ type: "p", lines: para });
  }
  return blocks;
}

const INLINE_PATTERNS = [
  { re: /^`([^`\n]+)`/, tag: "code", className: "rounded bg-black/10 px-0.5 font-mono text-[0.92em]" },
  { re: /^\*([^*\n]+)\*/, tag: "strong" },
  { re: /^_([^_\n]+)_/, tag: "em" },
  { re: /^~([^~\n]+)~/, tag: "s" },
];

/**
 * *negrito*, _itálico_, ~tachado~, `mono` — texto salvo permanece igual; só o preview interpreta.
 */
function parseInlinesToArray(text) {
  if (!text) return [];
  const out = [];
  let s = text;
  while (s.length) {
    let hit = null;
    for (const p of INLINE_PATTERNS) {
      const m = s.match(p.re);
      if (m && m.index === 0) {
        hit = { m, p };
        break;
      }
    }
    if (hit) {
      const inner = hit.m[1];
      const innerChildren = parseInlinesToArray(inner);
      const props = { key: nk() };
      if (hit.p.className) props.className = hit.p.className;
      out.push(React.createElement(hit.p.tag, props, innerChildren.length ? innerChildren : inner));
      s = s.slice(hit.m[0].length);
      continue;
    }
    let next = s.length;
    for (const ch of ["`", "*", "_", "~"]) {
      const ix = s.indexOf(ch);
      if (ix >= 0 && ix < next) next = ix;
    }
    const take = next === 0 ? 1 : next;
    out.push(s.slice(0, take));
    s = s.slice(take);
  }
  return out;
}

function normalizeInlineNodes(nodes) {
  return nodes.map((n, i) => (typeof n === "string" ? <Fragment key={`s-${i}`}>{n}</Fragment> : n));
}

/**
 * Texto bruto (com variáveis) → React com substituição + formatação estilo WhatsApp (preview apenas).
 */
export function parseWhatsAppToReact(rawText) {
  _nodeKey = 0;
  const sub = substituteVariables(rawText || "");
  const parts = sub.split(/(```[\s\S]*?```)/g);
  const out = [];
  parts.forEach((seg) => {
    if (!seg) return;
    if (seg.startsWith("```") && seg.endsWith("```")) {
      const inner = seg.slice(3, -3);
      out.push(
        <pre
          key={nk()}
          className="my-1 whitespace-pre-wrap break-words rounded-md bg-black/10 px-2 py-1.5 font-mono text-[12px] leading-snug text-slate-800"
        >
          {inner}
        </pre>,
      );
      return;
    }
    const blocks = parseLineBlocks(seg);
    blocks.forEach((b) => {
      if (b.type === "ul") {
        out.push(
          <ul key={nk()} className="my-1 list-disc pl-4 text-[13px] leading-relaxed text-slate-800">
            {b.items.map((it, ii) => (
              <li key={ii} className="mb-0.5 whitespace-pre-wrap">
                {normalizeInlineNodes(parseInlinesToArray(it))}
              </li>
            ))}
          </ul>,
        );
      } else if (b.type === "ol") {
        out.push(
          <ol key={nk()} className="my-1 list-decimal pl-4 text-[13px] leading-relaxed text-slate-800">
            {b.items.map((it, ii) => (
              <li key={ii} className="mb-0.5 whitespace-pre-wrap">
                {normalizeInlineNodes(parseInlinesToArray(it))}
              </li>
            ))}
          </ol>,
        );
      } else if (b.type === "quote") {
        out.push(
          <div key={nk()} className="my-1 border-l-4 border-emerald-600/45 pl-2 text-[13px] italic leading-relaxed text-slate-700">
            {b.lines.map((ln, li) => (
              <div key={li} className="whitespace-pre-wrap">
                {normalizeInlineNodes(parseInlinesToArray(ln))}
              </div>
            ))}
          </div>,
        );
      } else if (b.type === "p" && b.lines.length) {
        out.push(
          <p key={nk()} className="my-0 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-800 first:mt-0">
            {b.lines.map((ln, li) => (
              <Fragment key={li}>
                {li > 0 ? <br /> : null}
                {normalizeInlineNodes(parseInlinesToArray(ln))}
              </Fragment>
            ))}
          </p>,
        );
      } else if (b.type === "blank" && b.count > 0) {
        const n = Math.min(b.count, 16);
        out.push(
          <div
            key={nk()}
            aria-hidden
            className="shrink-0"
            style={{ minHeight: `${n * 0.85}em` }}
          />,
        );
      }
    });
  });
  return out.length ? out : <span className="text-slate-500">Digite sua mensagem…</span>;
}

export function WhatsAppTextBubble({ children, className = "" }) {
  return (
    <div className={`ml-auto max-w-[100%] rounded-lg rounded-tr-sm bg-[#dcf8c6] px-2.5 py-1.5 text-left shadow-sm ${className}`.trim()}>
      <div className="break-words">{children}</div>
    </div>
  );
}

/**
 * Bolha de áudio estilo WhatsApp; com URL (data: do editor ou HTTPS salvo), exibe player nativo para ouvir na prévia.
 */
export function WhatsAppAudioBubble({ mediaUrl, filename, className = "" }) {
  const name = filename?.trim() || "Mensagem de voz";

  return (
    <div
      className={`flex w-full flex-col gap-2 overflow-hidden rounded-lg rounded-tr-sm bg-[#dcf8c6] shadow-sm ${className}`.trim()}
    >
      {mediaUrl ?
        <>
          <div className="min-w-0 px-2 pt-2">
            <audio
              key={mediaUrl}
              controls
              preload="metadata"
              src={mediaUrl}
              className="h-10 min-h-10 w-full min-w-0"
            />
          </div>
          <div className="truncate px-2 pb-2 text-[11px] text-slate-600">{name}</div>
        </>
      : <div className="flex items-stretch gap-0">
          <span className="flex w-9 shrink-0 items-center justify-center bg-[#1fae52] text-white" aria-hidden>
            <span className="ml-0.5 inline-block h-0 w-0 border-y-[6px] border-l-[10px] border-y-transparent border-l-white" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-2 py-2">
            <div className="h-1 w-full rounded-full bg-black/10">
              <div className="h-full w-[35%] rounded-full bg-[#1fae52]/70" />
            </div>
            <div className="truncate text-[11px] text-slate-600">{name}</div>
          </div>
        </div>}
    </div>
  );
}

export function StepContentPreview({ contentType, textContent, caption, mediaUrl, filename }) {
  const ct = contentType || "text";
  if (ct === "text") {
    return <WhatsAppTextBubble>{parseWhatsAppToReact(textContent || "")}</WhatsAppTextBubble>;
  }
  if (ct === "image") {
    return (
      <div className="ml-auto max-w-[100%] overflow-hidden rounded-lg rounded-tr-sm bg-white shadow-sm">
        {mediaUrl ?
          <img src={mediaUrl} alt="" className="max-h-40 w-full object-cover" />
        : <div className="flex h-24 items-center justify-center text-[11px] text-slate-500">Imagem</div>}
      </div>
    );
  }
  if (ct === "image_caption") {
    return (
      <div className="ml-auto max-w-[100%] space-y-1">
        <div className="overflow-hidden rounded-lg rounded-tr-sm bg-white shadow-sm">
          {mediaUrl ?
            <img src={mediaUrl} alt="" className="max-h-36 w-full object-cover" />
          : <div className="flex h-24 items-center justify-center text-[11px] text-slate-500">Imagem</div>}
        </div>
        <WhatsAppTextBubble>{parseWhatsAppToReact(caption || "")}</WhatsAppTextBubble>
      </div>
    );
  }
  if (ct === "audio") {
    return (
      <div className="ml-auto w-full max-w-[min(100%,280px)]">
        <WhatsAppAudioBubble mediaUrl={mediaUrl} filename={filename} />
      </div>
    );
  }
  return null;
}
